import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus } from 'lucide-react';
import { budgetSetupService } from '../../services/budgetSetupService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { useColumnFilters, FilterPopover } from '../../components/gridFilters';
import PickerPopover from '../../components/PickerPopover';
import { exportGLTemplate, parseGLTemplate } from '../../utils/glLinesExcel';

// =============================================================================
// GLLinesSetupModal
// Configures the bud_gl_lines records for a chosen budget version.
//
// Flow:
//   1. The user picks the budget version they will work on (required).
//   2. Three modes:
//      - Add Records   : an inline "add line" bar stages new GL/Cost-Centre
//                        combinations into a grid, then batch-saves them.
//      - Import/Export : Excel round-trip of the same data.
//      - Manage & Edit : every configured line, editable in place (role,
//                        calculated flag, source module) or deletable.
//
// Both grids follow the People > Structures pattern: MantleDataGrid (canvas)
// with click-to-open pickers for the list-valued columns, a ✕ delete column,
// header ▾ column filters, and Save in the grid toolbar.
//
// Role scoping (bud_gl_lines.role_id): NULL = every role sees the line in OPEX
// entry; set = only that role (admins excepted). Assignment happens here.
// =============================================================================

const newId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
      }));

const BLANK_ENTRY = {
  account_code: '',
  cost_center_code: '',
  site_code: '',
  role_id: '',        // '' = all roles (stored as NULL)
  is_calculated: false,
  source_module: '',
};

// A CALCULATED line can be sourced from any module. A MANUAL line's module
// decides which ENTRY SCREEN it appears on, so only the three modules that have
// a capture grid qualify. Blank = OPEX, which is also where legacy lines with no
// module set are shown.
const ENTRY_MODULES = ['MOD_1_OPEX', 'MOD_3_MAKE', 'MOD_4_MOVE'];

const lineKey = (account, cc, site) => `${account}|${cc}|${site || ''}`;

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 ' +
  'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

const T_MUTED = { textDark: '#94a3b8' };
const T_CALC = { bgCell: 'rgba(100, 116, 139, 0.10)' };

const GLLinesSetupModal = ({ onClose, initialVersionId = '' }) => {
  const [viewMode, setViewMode] = useState('add'); // 'add' | 'import' | 'manage'

  // Reference + version data
  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState(initialVersionId);
  const [ref, setRef] = useState({ accounts: [], costCenters: [], sites: [], modules: [], roles: [] });

  // Existing configured lines for the selected version
  const [glLines, setGlLines] = useState([]);

  // Working state
  const [draftRows, setDraftRows] = useState([]);
  const [entry, setEntry] = useState(BLANK_ENTRY);
  const [edits, setEdits] = useState({}); // gl_line_id -> { role_id, is_calculated, source_module }

  // Status flags
  const [loadingRef, setLoadingRef] = useState(true);
  const [loadingLines, setLoadingLines] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Import / Export
  const [isPorting, setIsPorting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Popovers
  const [picker, setPicker] = useState(null);   // { field, scope, id, x, y }
  const [filterUI, setFilterUI] = useState(null);

  // ---- Load versions + reference data once --------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRef(true);
      try {
        const [vRes, rRes] = await Promise.all([
          budgetSetupService.getVersions(),
          budgetSetupService.getReferenceData(),
        ]);
        if (cancelled) return;
        if (vRes.success) {
          const active = vRes.data.filter((v) => v.is_active);
          setVersions(active);
          if (!initialVersionId && active.length) setVersionId(active[0].version_id);
        }
        if (rRes.success) setRef(rRes.data);
      } catch (e) {
        if (!cancelled) setError('Failed to load budget setup data.');
      } finally {
        if (!cancelled) setLoadingRef(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialVersionId]);

  // ---- Load configured lines whenever the version changes -----------------
  const loadLines = useCallback(async (vId) => {
    if (!vId) { setGlLines([]); return; }
    setLoadingLines(true);
    try {
      const res = await budgetSetupService.getGLLines(vId);
      if (res.success) setGlLines(res.data);
    } catch (e) {
      setError('Failed to load configured GL lines.');
    } finally {
      setLoadingLines(false);
    }
  }, []);

  useEffect(() => {
    setEdits({});
    loadLines(versionId);
  }, [versionId, loadLines]);

  // ---- Label maps ---------------------------------------------------------
  const accountLabel = useMemo(
    () => Object.fromEntries(ref.accounts.map((a) => [a.account_code, a.account_description])),
    [ref.accounts],
  );
  const ccLabel = useMemo(
    () => Object.fromEntries(ref.costCenters.map((c) => [c.cost_center_code, c.description])),
    [ref.costCenters],
  );
  const siteLabel = useMemo(
    () => Object.fromEntries(ref.sites.map((s) => [s.site_code, s.site_name])),
    [ref.sites],
  );
  const roleLabel = useMemo(
    () => Object.fromEntries((ref.roles || []).map((r) => [String(r.urid), r.ur_name])),
    [ref.roles],
  );
  const moduleLabel = useMemo(
    () => Object.fromEntries(ref.modules.map((m) => [m.module_code, m.module_name])),
    [ref.modules],
  );

  // PickerPopover options: `code` is the value picked, `label` the prose shown
  // in its place (a urid or module code would read as noise here).
  const roleOptions = useMemo(() => ([
    { code: '', label: 'All roles' },
    ...(ref.roles || []).map((r) => ({ code: String(r.urid), label: r.ur_name, hint: r.ur_code })),
  ]), [ref.roles]);

  const moduleOptionsFor = useCallback((isCalc) => ([
    { code: '', label: isCalc ? '— none —' : 'OPEX (default)' },
    ...ref.modules
      .filter((m) => isCalc || ENTRY_MODULES.includes(m.module_code))
      .map((m) => ({ code: m.module_code, label: m.module_name, hint: m.module_code })),
  ]), [ref.modules]);

  const existingKeys = useMemo(
    () => new Set(glLines.map((l) => lineKey(l.account_code, l.cost_center_code, l.site_code))),
    [glLines],
  );

  const showAccount = useCallback(
    (code) => (accountLabel[code] ? `${code} — ${accountLabel[code]}` : code || '—'),
    [accountLabel],
  );
  const showCc = useCallback(
    (code) => (ccLabel[code] ? `${code} — ${ccLabel[code]}` : code || '—'),
    [ccLabel],
  );
  const showSite = useCallback(
    (code) => (code ? (siteLabel[code] || code) : 'All sites'),
    [siteLabel],
  );
  const showRole = useCallback(
    (id) => (id == null || id === '' ? 'All roles' : (roleLabel[String(id)] || String(id))),
    [roleLabel],
  );
  const showModule = useCallback(
    (code) => (code ? (moduleLabel[code] || code) : '—'),
    [moduleLabel],
  );

  // =========================================================================
  // ADD RECORDS — inline add line + staged grid
  // =========================================================================
  const draftsRef = useRef([]);
  draftsRef.current = draftRows;

  const setE = (k, v) => setEntry((prev) => {
    const next = { ...prev, [k]: v };
    return next;
  });

  const entryProblem =
    !entry.account_code ? 'Pick an account.'
    : !entry.cost_center_code ? 'Pick a cost centre.'
    : draftsRef.current.some((d) =>
        lineKey(d.account_code, d.cost_center_code, d.site_code) ===
        lineKey(entry.account_code, entry.cost_center_code, entry.site_code))
      ? 'Already staged below.'
    : '';

  const addDraft = () => {
    if (entryProblem) return;
    setDraftRows((prev) => [...prev, { _id: newId(), ...entry }]);
    setEntry(BLANK_ENTRY);
    setNotice(null);
    setError(null);
  };

  const updateDraft = useCallback((id, field, value) => {
    setDraftRows((prev) => prev.map((d) => {
      if (d._id !== id) return d;
      const next = { ...d, [field]: value };
      return next;
    }));
  }, []);

  const removeDraft = useCallback((id) => {
    setDraftRows((prev) => prev.filter((d) => d._id !== id));
  }, []);

  const draftCols = useMemo(() => ([
    { id: 'account', title: 'Account', width: 200, grow: 1 },
    { id: 'cc', title: 'Cost Centre', width: 180, grow: 1 },
    { id: 'site', title: 'Site', width: 130 },
    { id: 'role', title: 'Role', width: 130 },
    { id: 'calc', title: 'Calculated', width: 88 },
    { id: 'module', title: 'Source Module', width: 150 },
    { id: 'del', title: '', width: 36 },
  ]), []);
  const draftColIds = useMemo(() => draftCols.map((c) => c.id), [draftCols]);

  const draftCell = useCallback(([col, row]) => {
    const d = draftRows[row];
    if (!d) return gcell.text('', { readonly: true });
    const id = draftColIds[col];
    switch (id) {
      case 'account': return gcell.text(showAccount(d.account_code), { readonly: true });
      case 'cc': return gcell.text(showCc(d.cost_center_code), { readonly: true });
      case 'site': return gcell.text(showSite(d.site_code), { readonly: true });
      case 'role': return gcell.text(showRole(d.role_id), { readonly: true, theme: d.role_id ? undefined : T_MUTED });
      case 'calc': return gcell.boolean(d.is_calculated);
      case 'module':
        return gcell.text(showModule(d.source_module) || 'OPEX (default)', { readonly: true });
      case 'del': return gcell.text('✕', { readonly: true, theme: T_MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [draftRows, draftColIds, showAccount, showCc, showSite, showRole, showModule]);

  const draftEdited = useCallback(([col, row], newVal) => {
    const d = draftsRef.current[row];
    if (!d) return;
    if (draftColIds[col] === 'calc') updateDraft(d._id, 'is_calculated', !!newVal.data);
  }, [draftColIds, updateDraft]);

  const draftClicked = useCallback(([col, row], event) => {
    const d = draftsRef.current[row];
    if (!d) return;
    const id = draftColIds[col];
    const b = event?.bounds;
    const at = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'role') setPicker({ field: 'role_id', scope: 'draft', id: d._id, ...at });
    else if (id === 'module') setPicker({ field: 'source_module', scope: 'draft', id: d._id, isCalc: !!d.is_calculated, ...at });
    else if (id === 'del') removeDraft(d._id);
  }, [draftColIds, removeDraft]);

  const handleSaveDrafts = async () => {
    setError(null);
    setNotice(null);
    if (!versionId) { setError('Select a budget version first.'); return; }
    if (draftRows.length === 0) { setError('Add at least one line above.'); return; }

    const lines = draftRows.map((r) => ({
      account_code: r.account_code,
      cost_center_code: r.cost_center_code,
      site_code: r.site_code || null,
      is_calculated: r.is_calculated,
      source_module: r.source_module || null,
      role_id: r.role_id === '' ? null : Number(r.role_id),
    }));

    setIsSaving(true);
    try {
      const res = await budgetSetupService.addGLLines({ version_id: versionId, lines });
      setNotice(res.message || `Saved ${lines.length} line(s).`);
      setDraftRows([]);
      await loadLines(versionId);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save GL lines.');
    } finally {
      setIsSaving(false);
    }
  };

  // =========================================================================
  // MANAGE & EDIT — grid over the saved lines
  // =========================================================================
  const editedValue = useCallback((line, field) =>
    (edits[line.gl_line_id] && field in edits[line.gl_line_id])
      ? edits[line.gl_line_id][field]
      : line[field], [edits]);

  const updateEdit = useCallback((lineId, field, value) => {
    setEdits((prev) => {
      const current = prev[lineId] || {};
      const next = { ...current, [field]: value };
      return { ...prev, [lineId]: next };
    });
  }, []);

  const accessors = useMemo(() => ({
    account: (l) => showAccount(l.account_code),
    cc: (l) => showCc(l.cost_center_code),
    site: (l) => showSite(l.site_code),
    role: (l) => showRole(l.role_id),
  }), [showAccount, showCc, showSite, showRole]);

  const { filters, setFilter, clearAll, filtered, activeIds } = useColumnFilters(glLines, accessors);
  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  const manageCols = useMemo(() => {
    const base = [
      { id: 'account', title: 'Account', width: 200, grow: 1 },
      { id: 'cc', title: 'Cost Centre', width: 180, grow: 1 },
      { id: 'site', title: 'Site', width: 130 },
      { id: 'role', title: 'Role', width: 130 },
      { id: 'calc', title: 'Calculated', width: 88 },
      { id: 'module', title: 'Source Module', width: 150 },
      { id: 'del', title: '', width: 36 },
    ];
    return base.map((c) => (accessors[c.id]
      ? { ...c, hasMenu: true, title: activeIds.includes(c.id) ? `${c.title} ●` : c.title }
      : c));
  }, [accessors, activeIds]);
  const manageColIds = useMemo(() => manageCols.map((c) => c.id), [manageCols]);

  const manageCell = useCallback(([col, row]) => {
    const l = filtered[row];
    if (!l) return gcell.text('', { readonly: true });
    const id = manageColIds[col];
    const isCalc = editedValue(l, 'is_calculated');
    const roleVal = editedValue(l, 'role_id');
    switch (id) {
      case 'account': return gcell.text(showAccount(l.account_code), { readonly: true });
      case 'cc': return gcell.text(showCc(l.cost_center_code), { readonly: true });
      case 'site': return gcell.text(showSite(l.site_code), { readonly: true });
      case 'role': return gcell.text(showRole(roleVal), { readonly: true, theme: roleVal ? undefined : T_MUTED });
      case 'calc': return gcell.boolean(isCalc);
      case 'module':
        return gcell.text(showModule(editedValue(l, 'source_module')) || 'OPEX (default)', {
          readonly: true, theme: isCalc ? undefined : T_CALC,
        });
      case 'del': return gcell.text('✕', { readonly: true, theme: T_MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [filtered, manageColIds, editedValue, showAccount, showCc, showSite, showRole, showModule]);

  const handleDeleteLine = useCallback(async (line) => {
    if (!window.confirm(`Delete ${line.account_code} / ${line.cost_center_code}? This removes its budget data.`)) return;
    setError(null);
    setNotice(null);
    try {
      await budgetSetupService.deleteGLLines([line.gl_line_id]);
      setNotice('Line deleted.');
      await loadLines(versionId);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete line.');
    }
  }, [loadLines, versionId]);

  const manageEdited = useCallback(([col, row], newVal) => {
    const l = filteredRef.current[row];
    if (!l) return;
    if (manageColIds[col] === 'calc') updateEdit(l.gl_line_id, 'is_calculated', !!newVal.data);
  }, [manageColIds, updateEdit]);

  const manageClicked = useCallback(([col, row], event) => {
    const l = filteredRef.current[row];
    if (!l) return;
    const id = manageColIds[col];
    const b = event?.bounds;
    const at = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'role') setPicker({ field: 'role_id', scope: 'line', id: l.gl_line_id, ...at });
    else if (id === 'module') {
      setPicker({ field: 'source_module', scope: 'line', id: l.gl_line_id,
                  isCalc: !!editedValue(l, 'is_calculated'), ...at });
    } else if (id === 'del') handleDeleteLine(l);
  }, [manageColIds, editedValue, handleDeleteLine]);

  const onHeaderMenu = useCallback((colIndex, bounds) => {
    const colId = manageColIds[colIndex];
    const acc = accessors[colId];
    if (!acc) return;
    const options = [...new Set(glLines.map((l) => String(acc(l) ?? '')))].sort();
    setFilterUI({
      colId,
      options,
      x: bounds ? bounds.x : 200,
      y: bounds ? bounds.y + bounds.height : 200,
    });
  }, [manageColIds, accessors, glLines]);

  const dirtyIds = Object.keys(edits);

  const handleSaveEdits = async () => {
    setError(null);
    setNotice(null);
    if (dirtyIds.length === 0) { setNotice('No changes to save.'); return; }

    // addGLLines upserts on (version, account, cc, site) — re-sending the
    // line's own dimensions updates its role / calc flag / source module.
    const lines = dirtyIds.map((id) => {
      const line = glLines.find((l) => l.gl_line_id === id);
      const is_calculated = editedValue(line, 'is_calculated');
      const roleVal = editedValue(line, 'role_id');
      return {
        account_code: line.account_code,
        cost_center_code: line.cost_center_code,
        site_code: line.site_code || null,
        is_calculated,
        source_module: editedValue(line, 'source_module') || null,
        role_id: roleVal === '' || roleVal == null ? null : Number(roleVal),
      };
    });

    setIsSaving(true);
    try {
      await budgetSetupService.addGLLines({ version_id: versionId, lines });
      setNotice(`Updated ${lines.length} line(s).`);
      setEdits({});
      await loadLines(versionId);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update GL lines.');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Picker plumbing ----------------------------------------------------
  const pickerOptions = picker?.field === 'role_id' ? roleOptions : moduleOptionsFor(!!picker?.isCalc);
  const pickerValue = (() => {
    if (!picker) return '';
    if (picker.scope === 'draft') {
      const d = draftRows.find((x) => x._id === picker.id);
      return d ? (d[picker.field] ?? '') : '';
    }
    const l = glLines.find((x) => x.gl_line_id === picker.id);
    return l ? (editedValue(l, picker.field) ?? '') : '';
  })();

  const onPick = (value) => {
    if (!picker) return;
    if (picker.scope === 'draft') updateDraft(picker.id, picker.field, value);
    else updateEdit(picker.id, picker.field, value);
    setPicker(null);
  };

  // ---- Import / Export ----------------------------------------------------
  const versionLabel = useMemo(() => {
    const v = versions.find((x) => x.version_id === versionId);
    return v ? v.version_code : versionId;
  }, [versions, versionId]);

  const handleExport = async () => {
    setError(null); setNotice(null); setImportErrors([]);
    setIsPorting(true);
    try {
      await exportGLTemplate({ versionLabel, ref, existingLines: glLines });
      setNotice('Template exported.');
    } catch (e) {
      setError('Failed to generate the Excel template.');
    } finally {
      setIsPorting(false);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null); setNotice(null); setImportErrors([]);
    setIsPorting(true);
    try {
      const { lines, errors } = await parseGLTemplate(file, ref);
      if (errors.length) {
        setImportErrors(errors);
        setError('Import cancelled — fix the rows below and try again.');
        return;
      }
      if (lines.length === 0) { setError('No data rows found in the template.'); return; }
      const res = await budgetSetupService.addGLLines({ version_id: versionId, lines });
      setNotice(res.message || `Imported ${lines.length} line(s).`);
      await loadLines(versionId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to read or import the template.');
    } finally {
      setIsPorting(false);
    }
  };

  // ---- Chrome -------------------------------------------------------------
  const TabButton = ({ id, children }) => (
    <button
      onClick={() => { setViewMode(id); setError(null); setNotice(null); }}
      className={`px-3 py-1.5 text-sm font-medium rounded ${
        viewMode === id ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
      }`}
    >
      {children}
    </button>
  );

  // Green whenever there is something to save, so the prompt is impossible to miss.
  const saveButtonClass = (hot) =>
    `px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      hot
        ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
        : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
    }`;

  const draftToolbar = (
    <button
      type="button"
      onClick={handleSaveDrafts}
      disabled={isSaving || draftRows.length === 0}
      className={saveButtonClass(draftRows.length > 0)}
    >
      {isSaving ? 'Saving…' : `Save ${draftRows.length || ''} line(s)`}
    </button>
  );

  const manageToolbar = (
    <>
      {activeIds.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="px-3 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
        >
          Clear filters ({activeIds.length})
        </button>
      )}
      <button
        type="button"
        onClick={handleSaveEdits}
        disabled={isSaving || dirtyIds.length === 0}
        className={saveButtonClass(dirtyIds.length > 0)}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans">
      <div className="bg-white dark:bg-slate-800 w-full max-w-6xl rounded-xl shadow-2xl flex flex-col h-[88vh] overflow-hidden">

        {/* Header + tabs */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">GL Budget Lines</h2>

          <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-md">
            <TabButton id="add">Add Records</TabButton>
            <TabButton id="import">Import / Export</TabButton>
            <TabButton id="manage">Manage &amp; Edit</TabButton>
          </div>

          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">✕</button>
        </div>

        {/* Version selector */}
        <div className="px-6 py-3 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3 shrink-0">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Budget Version</label>
          <select
            value={versionId}
            onChange={(e) => setVersionId(e.target.value)}
            disabled={loadingRef || versions.length === 0}
            className={`${inputClass} min-w-[280px] w-auto py-1.5`}
          >
            {versions.length === 0 && <option value="">No active versions</option>}
            {versions.map((v) => (
              <option key={v.version_id} value={v.version_id}>
                {v.version_code} — {v.description} (FY{v.financial_year})
              </option>
            ))}
          </select>
          {loadingLines && <span className="text-xs text-slate-400">Loading lines…</span>}
          {!loadingLines && versionId && (
            <span className="text-xs text-slate-400">{glLines.length} configured line(s)</span>
          )}
        </div>

        {/* Alerts */}
        {(error || notice) && (
          <div className="px-6 pt-3 shrink-0">
            {error && (
              <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>
            )}
            {notice && !error && (
              <div className="p-2.5 bg-green-50 border border-green-200 text-green-700 text-sm rounded-md">{notice}</div>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col">
          {!versionId ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              {loadingRef ? 'Loading…' : 'Create and select a budget version to begin.'}
            </div>
          ) : viewMode === 'import' ? (
            <div className="flex-1 overflow-auto px-6 py-6">
              <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-5 flex flex-col">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export Template</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400 flex-1">
                    Download an Excel workbook pre-filled with this version's lines. Account, Cost
                    Centre, Site, Role, Calculated and Source Module all carry dropdowns.
                  </p>
                  <button onClick={handleExport} disabled={isPorting}
                    className="mt-4 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isPorting ? 'Working…' : 'Export to Excel'}
                  </button>
                </div>

                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-5 flex flex-col">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import Template</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400 flex-1">
                    Upload a completed template. Every row is validated against master data; if
                    anything is off, nothing is saved. Existing combinations are updated.
                  </p>
                  <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileSelected} />
                  <button onClick={() => fileInputRef.current?.click()} disabled={isPorting}
                    className="mt-4 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isPorting ? 'Working…' : 'Import from Excel'}
                  </button>
                </div>
              </div>

              {importErrors.length > 0 && (
                <div className="max-w-2xl mx-auto mt-5 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm font-medium text-red-700 mb-1.5">
                    {importErrors.length} row(s) need attention — nothing was imported:
                  </p>
                  <ul className="text-xs text-red-600 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
                    {importErrors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : viewMode === 'add' ? (
            <>
              {/* Inline add line — same shape as People > Structures. */}
              <div className="shrink-0 px-6 pt-4 pb-3">
                <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
                  <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                    Account <span className="text-red-600 dark:text-red-400">*</span>
                    <select className={`${inputClass} w-56`} value={entry.account_code}
                      onChange={(e) => setE('account_code', e.target.value)}>
                      <option value="">Select account…</option>
                      {ref.accounts.map((a) => (
                        <option key={a.account_code} value={a.account_code}>
                          {a.account_code} — {a.account_description}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                    Cost Centre <span className="text-red-600 dark:text-red-400">*</span>
                    <select className={`${inputClass} w-52`} value={entry.cost_center_code}
                      onChange={(e) => setE('cost_center_code', e.target.value)}>
                      <option value="">Select cost centre…</option>
                      {ref.costCenters.map((c) => (
                        <option key={c.cost_center_code} value={c.cost_center_code}>
                          {c.cost_center_code} — {c.description}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                    Site
                    <select className={`${inputClass} w-40`} value={entry.site_code}
                      onChange={(e) => setE('site_code', e.target.value)}>
                      <option value="">All sites</option>
                      {ref.sites.map((s) => (
                        <option key={s.site_code} value={s.site_code}>{s.site_name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                    Role (OPEX visibility)
                    <select className={`${inputClass} w-40`} value={entry.role_id}
                      onChange={(e) => setE('role_id', e.target.value)}>
                      <option value="">All roles</option>
                      {(ref.roles || []).map((r) => (
                        <option key={r.urid} value={r.urid}>{r.ur_name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                    Source Module
                    <select className={`${inputClass} w-40`} value={entry.source_module}
                      onChange={(e) => setE('source_module', e.target.value)}>
                      <option value="">{entry.is_calculated ? 'Select module…' : 'OPEX (default)'}</option>
                      {ref.modules
                        .filter((m) => entry.is_calculated || ENTRY_MODULES.includes(m.module_code))
                        .map((m) => (
                          <option key={m.module_code} value={m.module_code}>{m.module_name}</option>
                        ))}
                    </select>
                  </label>

                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 pb-2">
                    <input type="checkbox" className="h-4 w-4 accent-emerald-600"
                      checked={entry.is_calculated}
                      onChange={(e) => setE('is_calculated', e.target.checked)} />
                    Calculated
                  </label>

                  <button
                    type="button"
                    onClick={addDraft}
                    disabled={!!entryProblem}
                    title={entryProblem || 'Add line'}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${
                      entryProblem ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                {entry.account_code && entry.cost_center_code &&
                  existingKeys.has(lineKey(entry.account_code, entry.cost_center_code, entry.site_code)) && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    Already configured for this version — saving will update it.
                  </p>
                )}
              </div>

              <div className="flex-1 min-h-0 px-6 pb-5">
                <div className="max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
                  {draftRows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">
                      Nothing staged yet — add a line above, then Save.
                    </div>
                  ) : (
                    <MantleDataGrid
                      gridId="gl-lines-add"
                      fitHeight
                      columns={draftCols}
                      rowCount={draftRows.length}
                      getCellContent={draftCell}
                      onCellEdited={draftEdited}
                      onCellClicked={draftClicked}
                      freezeColumns={1}
                      hint="Click Role or Source Module to change it, tick Calculated, ✕ removes a staged line. Nothing is written until you Save."
                      toolbar={draftToolbar}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 min-h-0 px-6 py-4">
              <div className="max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
                {loadingLines ? (
                  <div className="p-4 text-xs text-slate-400">Loading…</div>
                ) : glLines.length === 0 ? (
                  <div className="p-8 text-center text-sm text-slate-400">
                    No GL lines configured for this version yet.
                  </div>
                ) : (
                  <MantleDataGrid
                    gridId="gl-lines-manage"
                    fitHeight
                    columns={manageCols}
                    rowCount={filtered.length}
                    getCellContent={manageCell}
                    onCellEdited={manageEdited}
                    onCellClicked={manageClicked}
                    onHeaderMenuClick={onHeaderMenu}
                    freezeColumns={1}
                    hint="Click Role or Source Module to change it, tick Calculated, ✕ deletes the line. Header ▾ filters a column. Role “All roles” means every role sees it in OPEX entry."
                    toolbar={manageToolbar}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Column filter popover (Manage & Edit) */}
      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {/* Role / Source Module picker */}
      {picker && (
        <PickerPopover
          anchor={picker}
          options={pickerOptions}
          value={pickerValue}
          onPick={onPick}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

export default GLLinesSetupModal;
